import { FC, MouseEvent, useState } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubColorProps,
  DevHubDynamicIcon,
  devHubDynamicSvgIcon,
  DevHubList,
  DevHubListItem,
  DevHubListItemText,
  DevHubPopover,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';

type SupportItemLink = {
  url: string;
  title: string;
};

type SupportItem = {
  title: string;
  icon: () => JSX.Element;
  links: SupportItemLink[];
};

interface DevHubSupportButtonProps {
  color?: DevHubColorProps;
}

/**
 * Custom support button component
 * @param props - DevHubSupportButtonProps
 * @constructor
 */
const DevHubSupportButton: FC<DevHubSupportButtonProps> = (
  props: DevHubSupportButtonProps,
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const supportItems: SupportItem[] = [
    {
      title: 'Register for TKTS',
      icon: devHubDynamicSvgIcon('Assignment'),
      links: [
        {
          title: 'Self-register into the TKTS instance',
          url: 'https://sd-comcast.atlassian.net/servicedesk/customer/portal/34/group/71/create/112',
        },
      ],
    },
    {
      title: 'Report Issue',
      icon: devHubDynamicSvgIcon('ReportProblem'),
      links: [
        {
          title: 'Jira',
          url: 'https://t.cmcst.net/c/devhub-support',
        },
      ],
    },
    {
      title: 'Slack Channel',
      icon: devHubDynamicSvgIcon('Message'),
      links: [
        { title: 'Link 1', url: 'https://cim.slack.com/archives/C02N9B1E4QH' },
      ],
    },
    {
      title: 'Teams Support',
      icon: devHubDynamicSvgIcon('People'),
      links: [
        {
          title: 'Link 1',
          url: 'https://teams.microsoft.com/l/channel/19%3A9c2c316f92954d2f9ae6080dd6444ff7%40thread.skype/General?groupId=d3388271-730e-40b5-a856-73be785c0c25&tenantId=',
        },
      ],
    },
  ];

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (url: string | URL | undefined) => {
    handleClose();
    window.open(url, '_blank');
  };

  const open = Boolean(anchorEl);
  const id = open ? 'support-popover' : undefined;

  return (
    <>
      <DevHubButton
        textColor={props.color || 'primary'}
        onClick={handleClick}
        aria-describedby={id}
      >
        <DevHubDynamicIcon iconName="Help" /> Support
      </DevHubButton>
      <DevHubPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <DevHubBox p={1}>
          <DevHubList>
            {supportItems.map(item => {
              const listItem = (
                <DevHubListItem
                  key={item.title}
                  button
                  onClick={() => handleOptionClick(item.links[0].url)}
                >
                  <DevHubBox pr={1}>
                    <item.icon />
                  </DevHubBox>
                  <DevHubListItemText primary={item.title} />
                </DevHubListItem>
              );

              if (item.title === 'Report Issue') {
                return (
                  <DevHubTooltip
                    key={item.title}
                    title="You may need to self-register into the TKTS Jira instance before reporting an issue. Use 'Register for TKTS' above if this is your first time."
                    placement="right"
                  >
                    <span>{listItem}</span>
                  </DevHubTooltip>
                );
              }

              return listItem;
            })}
          </DevHubList>
          <DevHubBox display="flex" justifyContent="flex-end">
            <DevHubButton onClick={handleClose} color="primary" variant="text">
              Close
            </DevHubButton>
          </DevHubBox>
        </DevHubBox>
      </DevHubPopover>
    </>
  );
};

export default DevHubSupportButton;
